// Feature 6 — pure helpers that regroup the Feature 5 monthly schedule
// (from /api/loans/chart-series) into chart buckets. No loan math here:
// only sums of the rows produced by the shared loan_math schedule.

export const SERIES = [
  { key: "principal", label: "Principal paid", color: "#2B7BB9", dash: null, fdash: "2 3" },
  { key: "interest", label: "Interest paid", color: "#D9622B", dash: "6 3", fdash: "6 3 1 3" },
  { key: "balance", label: "Outstanding balance", color: "#8E5BA8", dash: "2 3", fdash: "1 4" },
  { key: "prepayment", label: "Prepayments", color: "#2E9E6B", dash: null, fdash: "2 3" },
  { key: "emi", label: "EMI", color: "#8A8A8A", dash: "8 3 2 3", fdash: "8 4" },
];
export const LOAN_COLORS = ["#2B7BB9", "#D9622B", "#2E9E6B", "#C9A227", "#8E5BA8", "#4FA3A5", "#B5577B", "#6B7F2A"];
export const LOAN_DASHES = [null, "6 3", "2 3", "8 3 2 3", "10 4", "4 2", null, "6 3"];

export const DEFAULT_SETTINGS = {
  chartType: "line",
  measure: "per_period",
  series: { principal: true, interest: true, balance: false, prepayment: false, emi: false },
  timeAxis: "fy",
  window: "full",
  display: "rupees",
  split: false,
  splitMetric: "principal",
  rangeFrom: "",
  rangeTo: "",
  applyRateChanges: false,
};

const ENUMS = {
  chartType: ["line", "area", "stacked", "grouped", "combo", "donut"],
  measure: ["per_period", "running"],
  timeAxis: ["monthly", "loan_year", "cy", "fy", "custom"],
  window: ["past", "future", "full"],
  display: ["rupees", "pct"],
  splitMetric: ["principal", "interest"],
};

export function sanitizeSettings(raw) {
  const s = { ...DEFAULT_SETTINGS, series: { ...DEFAULT_SETTINGS.series } };
  if (!raw || typeof raw !== "object") return s;
  Object.entries(ENUMS).forEach(([k, vals]) => { if (vals.includes(raw[k])) s[k] = raw[k]; });
  ["split", "applyRateChanges"].forEach((k) => { if (typeof raw[k] === "boolean") s[k] = raw[k]; });
  ["rangeFrom", "rangeTo"].forEach((k) => { if (/^\d{4}-\d{2}$/.test(raw[k] || "")) s[k] = raw[k]; });
  if (raw.series && typeof raw.series === "object") {
    Object.keys(s.series).forEach((k) => { if (typeof raw.series[k] === "boolean") s.series[k] = raw.series[k]; });
  }
  return s;
}

export const isDefaultSettings = (s) => JSON.stringify(sanitizeSettings(s)) === JSON.stringify(DEFAULT_SETTINGS);

// Returns a reason string for every option that is invalid with the other current choices.
export function disabledReasons(s) {
  const split = s.split;
  const pct = s.display === "pct";
  const donut = s.chartType === "donut";
  const r = { chartType: {}, measure: {}, display: {}, loans: {}, series: {}, time: null };
  if (s.measure === "running") r.chartType.donut = "Donut shows the paid vs to-pay split at the As-on date — it can't be a running total.";
  if (split) {
    r.chartType.donut = "Donut can't split by loan — choose Cumulative - combined.";
    r.chartType.combo = "Combo shows principal/interest bars with the balance line — not available split by loan.";
  }
  if (pct) r.chartType.combo = "Outstanding balance can't be shown as % of EMI.";
  if (donut) {
    r.measure.running = "Donut is a single As-on split — a running total doesn't apply.";
    r.loans.split = "Donut can't split by loan.";
    r.time = "Donut is not time-based.";
  }
  if (s.chartType === "combo") {
    r.loans.split = "Combo isn't available split by loan.";
    r.display.pct = "Combo includes the outstanding balance, which can't be a % of EMI.";
  }
  if (split) r.display.pct = "% of EMI is available for a single loan or the combined total.";
  if (pct) r.loans.split = "Switch Value display to Rupees to split by loan.";
  SERIES.forEach(({ key }) => {
    if (split) r.series[key] = "Split by loan shows one measure per loan — use the Principal / Interest toggle.";
    else if (pct && !["principal", "interest"].includes(key)) r.series[key] = "Only principal and interest can be shown as % of EMI.";
    else if (donut && !["principal", "interest"].includes(key)) r.series[key] = "Donut shows principal and interest only.";
  });
  if (s.chartType === "combo" && !split) r.series.balance = "Combo always shows the outstanding balance line.";
  return r;
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const mi = (iso) => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1;

function bucketOf(iso, fromBase, axis) {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  if (axis === "loan_year") { const n = Math.floor(fromBase / 12) + 1; return { key: n, label: `Year ${n}` }; }
  if (axis === "cy") return { key: y, label: String(y) };
  if (axis === "fy") { const fy = m >= 4 ? y + 1 : y; return { key: fy, label: `FY${String(fy % 100).padStart(2, "0")}` }; }
  return { key: y * 12 + m - 1, label: `${MON[m - 1]} ${y}` };
}

const pctOf = (a, b) => (a + b > 0 ? Math.round((a / (a + b)) * 1000) / 10 : 0);

function annotate(loan, base, axis) {
  let cp = 0, ci = 0, cpre = 0, ce = 0;
  return loan.rows.map((r) => {
    cp += r.principal; ci += r.interest; cpre += r.prepayment; ce += r.emi;
    const m = mi(r.date);
    const b = bucketOf(r.date, m - base, axis);
    return { ...r, m, cp, ci, cpre, ce, bkey: b.key, blabel: b.label };
  });
}

function keepRow(s) {
  const from = s.rangeFrom ? mi(`${s.rangeFrom}-01`) : -Infinity;
  const to = s.rangeTo ? mi(`${s.rangeTo}-01`) : Infinity;
  return (r) => (s.window === "past" ? r.is_past : s.window === "future" ? !r.is_past : true)
    && (s.timeAxis !== "custom" || (r.m >= from && r.m <= to));
}

function lastByKey(rows) {
  const map = new Map();
  rows.forEach((r) => map.set(r.bkey, r));
  return map;
}

function sumByKey(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const b = map.get(r.bkey) || { principal: 0, interest: 0, prepayment: 0, emi: 0, months: 0, past: 0 };
    b.principal += r.principal; b.interest += r.interest; b.prepayment += r.prepayment; b.emi += r.emi;
    b.months += 1; if (r.is_past) b.past += 1;
    map.set(r.bkey, b);
  });
  return map;
}

/** Bucket rows for the chosen loans. mode = individual | combined | split. */
export function buildChartData(apiLoans, s, mode, loanIdx = 0) {
  const loans = mode === "individual" ? [apiLoans?.[loanIdx]].filter(Boolean) : (apiLoans || []);
  const withRows = loans.filter((l) => l.rows?.length);
  if (!withRows.length) return { rows: [], loans: [], events: [], crossover: null, today: null };
  const base = Math.min(...withRows.map((l) => mi(l.rows[0].date)));
  const keep = keepRow(s);
  const per = withRows.map((l) => {
    const all = annotate(l, base, s.timeAxis);
    const filt = all.filter(keep);
    return { loan: l, all, filt, lastAll: lastByKey(all), lastFilt: lastByKey(filt), sums: sumByKey(filt) };
  });
  const labels = new Map();
  per.forEach((p) => p.all.forEach((r) => labels.set(r.bkey, r.blabel)));
  const allKeys = [...labels.keys()].sort((a, b) => a - b);
  const shown = new Set();
  per.forEach((p) => p.filt.forEach((r) => shown.add(r.bkey)));

  const carry = per.map(() => ({ cp: 0, ci: 0, cpre: 0, ce: 0, balance: 0 }));
  const rows = [];
  allKeys.forEach((key) => {
    per.forEach((p, j) => {
      const last = p.lastFilt.get(key) || p.lastAll.get(key);
      if (last) carry[j] = { cp: last.cp, ci: last.ci, cpre: last.cpre, ce: last.ce, balance: last.balance };
    });
    if (!shown.has(key)) return;
    const row = { key, label: labels.get(key), principal: 0, interest: 0, prepayment: 0, emi: 0,
      balance: 0, cum_principal: 0, cum_interest: 0, cum_prepayment: 0, cum_emi: 0, months: 0, past: 0 };
    per.forEach((p, j) => {
      const sm = p.sums.get(key) || { principal: 0, interest: 0, prepayment: 0, emi: 0, months: 0, past: 0 };
      const c = carry[j];
      row.principal += sm.principal; row.interest += sm.interest; row.prepayment += sm.prepayment;
      row.emi += sm.emi; row.months += sm.months; row.past += sm.past;
      row.balance += c.balance; row.cum_principal += c.cp; row.cum_interest += c.ci;
      row.cum_prepayment += c.cpre; row.cum_emi += c.ce;
      row[`L${j}_principal`] = Math.round(sm.principal);
      row[`L${j}_interest`] = Math.round(sm.interest);
      row[`L${j}_cum_principal`] = Math.round(c.cp);
      row[`L${j}_cum_interest`] = Math.round(c.ci);
    });
    ["principal", "interest", "prepayment", "emi", "balance", "cum_principal", "cum_interest", "cum_prepayment", "cum_emi"]
      .forEach((k) => { row[k] = Math.round(row[k]); });
    row.pct_principal = pctOf(row.principal, row.interest);
    row.pct_interest = row.principal + row.interest > 0 ? Math.round((100 - row.pct_principal) * 10) / 10 : 0;
    row.cum_pct_principal = pctOf(row.cum_principal, row.cum_interest);
    row.cum_pct_interest = row.cum_principal + row.cum_interest > 0 ? Math.round((100 - row.cum_pct_principal) * 10) / 10 : 0;
    row.status = row.past === row.months ? "Paid" : row.past > 0 ? "Partial" : "Future";
    rows.push(row);
  });

  const labelOfMonth = (p, month) => {
    const r = p.all[month - 1];
    return r && shown.has(r.bkey) ? r.blabel : null;
  };
  const events = [];
  per.forEach((p) => {
    p.filt.filter((r) => r.prepayment > 0).forEach((r) => events.push({
      label: r.blabel, kind: "prepayment", loanIdx: per.indexOf(p),
      text: `${p.loan.loan_name}: prepayment ₹${Math.round(r.prepayment).toLocaleString("en-IN")} (EMI ${r.i})`,
    }));
    (p.loan.rate_events || []).forEach((e) => {
      const label = labelOfMonth(p, e.month);
      if (label) events.push({ label, kind: "rate", loanIdx: per.indexOf(p),
        text: `${p.loan.loan_name}: rate ${e.old_rate}% → ${e.new_rate}% from ${e.date}` });
    });
  });
  const cross = mode === "individual" && withRows[0].crossover_month ? withRows[0].crossover_month : null;
  const crossover = cross ? { label: labelOfMonth(per[0], cross), month: cross } : null;
  const lastPast = [...rows].reverse().find((r) => r.past > 0);
  const today = lastPast && rows.some((r) => r.status !== "Paid") ? lastPast.label : null;
  return { rows, loans: withRows.map((l) => l.loan_name), events, crossover: crossover?.label ? crossover : null, today };
}

const valueField = (key, s) => {
  if (key === "balance") return "balance";
  if (s.display === "pct") return `${s.measure === "running" ? "cum_" : ""}pct_${key}`;
  return s.measure === "running" ? `cum_${key}` : key;
};

/** Adds v_* fields used for drawing. The table never reads these. */
export function chartValues(rows, s, nLoans) {
  const lastPaid = rows.reduce((acc, r, i) => (r.status === "Paid" ? i : acc), -1);
  const keys = s.split ? [...Array(nLoans).keys()].map((j) => `L${j}`) : SERIES.map((x) => x.key);
  return rows.map((r, i) => {
    const out = { ...r };
    keys.forEach((k) => {
      const v = s.split ? r[`${k}_${s.measure === "running" ? "cum_" : ""}${s.splitMetric}`] : r[valueField(k, s)];
      out[`v_${k}`] = v;
      out[`v_${k}__p`] = i <= lastPaid ? v : null;
      out[`v_${k}__f`] = i >= lastPaid ? v : null;
    });
    return out;
  });
}

export function tableColumns(mode, loanNames) {
  const cols = [
    { key: "label", label: "Period", fmt: "text" },
    { key: "principal", label: "Principal (period)" },
    { key: "interest", label: "Interest (period)" },
    { key: "emi", label: "EMI paid (period)" },
    { key: "prepayment", label: "Prepayment" },
    { key: "balance", label: "Outstanding (end)" },
    { key: "cum_principal", label: "Running principal" },
    { key: "cum_interest", label: "Running interest" },
    { key: "pct_principal", label: "Principal % of EMI", fmt: "pct" },
    { key: "pct_interest", label: "Interest % of EMI", fmt: "pct" },
  ];
  if (mode === "split") {
    loanNames.forEach((n, j) => {
      cols.push({ key: `L${j}_principal`, label: `${n} principal` });
      cols.push({ key: `L${j}_interest`, label: `${n} interest` });
    });
  }
  cols.push({ key: "status", label: "Status", fmt: "text" });
  return cols;
}

/** Donut: paid vs to-pay at the As-on date (same rows, summed by is_past). */
export function donutData(apiLoans, mode, loanIdx = 0) {
  const loans = mode === "individual" ? [apiLoans?.[loanIdx]].filter(Boolean) : (apiLoans || []);
  const t = { pp: 0, ip: 0, pf: 0, if: 0 };
  loans.forEach((l) => (l.rows || []).forEach((r) => {
    if (r.is_past) { t.pp += r.principal; t.ip += r.interest; } else { t.pf += r.principal; t.if += r.interest; }
  }));
  const total = t.pp + t.ip + t.pf + t.if || 1;
  return [
    { key: "pp", label: "Principal paid", value: Math.round(t.pp), color: "#2B7BB9" },
    { key: "ip", label: "Interest paid", value: Math.round(t.ip), color: "#D9622B" },
    { key: "pf", label: "Principal to pay", value: Math.round(t.pf), color: "#9CC3E4" },
    { key: "if", label: "Interest to pay", value: Math.round(t.if), color: "#F0B496" },
  ].map((d) => ({ ...d, pct: Math.round((d.value / total) * 1000) / 10 }));
}

export function axisINR(v) {
  const a = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(a >= 1e8 ? 0 : 1)}Cr`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(a >= 1e6 ? 0 : 1)}L`;
  if (a >= 1e3) return `${sign}₹${Math.round(a / 1e3)}K`;
  return `${sign}₹${Math.round(a)}`;
}
